import { useState } from "react";
import ParseUtils from "../../../utils/ParseUtils";

const ALLOWED_VALUES = new Set([undefined, null, "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", ",", "."]);

function withNumberFormatValidate(Component) {
  function WrapperComponent(props) {
    const { onChange, min, max, allowDecimals, helperText: originalHelperText, error: externalError } = props;

    const [error, setError] = useState(null);

    function handleOnChange(event) {
      const inputValue = event.nativeEvent.data;
      const fullValue = event.target.value;

      // For paste events, event.nativeEvent.data might be longer than 1 character
      // In that case, validate the entire value instead of just the inserted character
      if (inputValue !== null && inputValue.length > 1) {
        // Paste event - validate the entire value by checking if it can be parsed
        // PatternFormat formats the value, so we need to check the raw numeric content
        _checkFullValue(fullValue);
      } else {
        // Typing event - validate the inserted character
        _checkNumberValue(inputValue) && _checkBounds(fullValue) && _checkDecimals(fullValue);
      }

      onChange && onChange(event);
    }

    function _checkNumberValue(inputValue) {
      let valid = false;
      if (ALLOWED_VALUES.has(inputValue)) valid = true;

      if (!valid) setError("Zadejte číselnou hodnotu.");
      if (valid && error) setError(null);

      return valid;
    }

    function _checkFullValue(fullValue) {
      // For paste events, validate the entire value
      if (!fullValue || fullValue === "") {
        if (error) setError(null);
        return true;
      }

      // Remove format characters (spaces, slashes, etc.) and check if remaining is numeric
      // PatternFormat may include format characters, so we extract just the digits
      const numericPart = fullValue.replace(/[^\d.,]/g, "");

      // Check if there's at least some numeric content
      if (numericPart === "") {
        setError("Zadejte číselnou hodnotu.");
        return false;
      }

      // We have numeric content, so clear any "numeric value" error
      // Other validation functions will handle their own errors
      const currentError = error;
      if (currentError === "Zadejte číselnou hodnotu.") {
        setError(null);
      }

      // Validate bounds and decimals - these functions will set/clear errors as needed
      _checkBounds(fullValue);
      _checkDecimals(fullValue);

      return true;
    }

    function _checkDecimals(inputValue) {
      const floatValue = ParseUtils.parseNumber(inputValue);
      if (floatValue == null) {
        if (error) setError(null);
        return;
      }

      if (allowDecimals) {
        if (error) setError(null);
        return true;
      }

      if (floatValue.toString().includes(".")) {
        setError("Zadejte celé číslo.");
        return;
      }
    }

    function _checkBounds(inputValue) {
      const floatValue = ParseUtils.parseNumber(inputValue);
      if (floatValue == null) {
        if (error) setError(null);
        return;
      }

      // Check min bound
      if (min !== undefined && floatValue < min) {
        setError(`Hodnota musí být minimálně ${min}.`);
        return;
      }

      // Check max bound
      if (max !== undefined && floatValue > max) {
        setError(`Hodnota musí být maximálně ${max}.`);
        return;
      }

      // Clear error if value is valid
      if (error) setError(null);
      return true;
    }

    // Combine internal error with external error prop
    const hasError = !!error || !!externalError;
    const displayHelperText = error || originalHelperText;

    return <Component {...props} onChange={handleOnChange} error={hasError} helperText={displayHelperText} />;
  }

  WrapperComponent.displayName = Component.displayName;

  return WrapperComponent;
}

export default withNumberFormatValidate;
